import { createContext, useCallback, useContext, useMemo, useState } from "react";

const STORAGE_PREFIX = "jumlaty_draft_";

const safeRead = (key) => {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + key);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
};
const safeWrite = (key, value) => {
  try { localStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(value)); } catch { /* quota / private mode */ }
};
const safeDelete = (key) => {
  try { localStorage.removeItem(STORAGE_PREFIX + key); } catch { /* noop */ }
};
const safeScan = () => {
  const out = {};
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(STORAGE_PREFIX)) {
        const id = k.slice(STORAGE_PREFIX.length);
        const v = safeRead(id);
        if (v && v.label) out[id] = { label: v.label, updatedAt: v.updatedAt || Date.now() };
      }
    }
  } catch { /* noop */ }
  return out;
};

const DraftsContext = createContext({
  drafts: {},
  markDirty: () => {},
  clearDirty: () => {},
  pendingTarget: null,
  setPendingTarget: () => {},
  confirmSwitch: () => false,
  proceedSwitch: () => {},
  cancelSwitch: () => {},
  closeConfirm: null,
  requestClose: () => {},
  resolveClose: () => {},
  saveDraft: () => {},
  loadDraft: () => null,
});

export const DraftsProvider = ({ children }) => {
  // Drafts hydrated from localStorage on first render
  const [drafts, setDrafts] = useState(() => safeScan());
  const [pendingTarget, setPendingTarget] = useState(null);
  const [closeConfirm, setCloseConfirm] = useState(null);

  const markDirty = useCallback((tabId, label) => {
    if (!tabId) return;
    setDrafts((prev) => {
      if (prev[tabId]?.label === label) return prev;
      return { ...prev, [tabId]: { label, updatedAt: Date.now() } };
    });
  }, []);

  const clearDirty = useCallback((tabId) => {
    if (!tabId) return;
    safeDelete(tabId);
    setDrafts((prev) => {
      if (!(tabId in prev)) return prev;
      const next = { ...prev };
      delete next[tabId];
      return next;
    });
  }, []);

  /**
   * saveDraft(tabId, label, payload) — persists field content to localStorage.
   * Also marks the tab as dirty so the chip appears.
   */
  const saveDraft = useCallback((tabId, label, payload) => {
    if (!tabId) return;
    const record = { label, payload, updatedAt: Date.now() };
    safeWrite(tabId, record);
    setDrafts((prev) => ({ ...prev, [tabId]: { label, updatedAt: record.updatedAt } }));
  }, []);

  /**
   * loadDraft(tabId) — returns the saved payload (or null) for a tab.
   */
  const loadDraft = useCallback((tabId) => {
    const v = safeRead(tabId);
    return v ? v.payload : null;
  }, []);

  const confirmSwitch = useCallback(
    (_currentTab, _targetTab, _onProceed) => {
      // Non-blocking: drafts are auto-saved to localStorage; let the user
      // navigate between tabs freely. Pending work stays as chips in the
      // PendingDraftsBar so they can resume any tab whenever they want.
      return false;
    },
    []
  );

  const proceedSwitch = useCallback(() => {
    if (!pendingTarget) return;
    clearDirty(pendingTarget.from);
    const cb = pendingTarget.onConfirm;
    setPendingTarget(null);
    cb && cb();
  }, [pendingTarget, clearDirty]);

  const cancelSwitch = useCallback(() => {
    setPendingTarget(null);
  }, []);

  const requestClose = useCallback((_hasContent, _label, onDiscard) => {
    // Non-blocking: form content is already persisted via saveDraft. Closing
    // the modal does NOT erase the draft — the user can reopen anytime and
    // continue from where they left off. So we simply close immediately.
    onDiscard && onDiscard();
  }, []);

  const resolveClose = useCallback((shouldDiscard) => {
    const cb = closeConfirm?.onDiscard;
    setCloseConfirm(null);
    if (shouldDiscard) cb && cb();
  }, [closeConfirm]);

  const value = useMemo(
    () => ({
      drafts, markDirty, clearDirty,
      pendingTarget, setPendingTarget, confirmSwitch, proceedSwitch, cancelSwitch,
      closeConfirm, requestClose, resolveClose,
      saveDraft, loadDraft,
    }),
    [drafts, markDirty, clearDirty, pendingTarget, confirmSwitch, proceedSwitch, cancelSwitch, closeConfirm, requestClose, resolveClose, saveDraft, loadDraft]
  );

  return <DraftsContext.Provider value={value}>{children}</DraftsContext.Provider>;
};

export const useDrafts = () => useContext(DraftsContext);
